import { useAreas, useProjects, useServices } from "@hq/client-react";
import { Button, Dialog, Input } from "@z/ui";
import { useId, useRef, useState } from "react";

export function NewAreaDialog({ onClose }: { onClose: () => void }) {
  const services = useServices();
  const { data: areas = [], isLoading } = useAreas();
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const id = useId();
  const save = async () => {
    if (inFlight.current || isLoading || projectsLoading || !name.trim()) return;
    const trimmed = name.trim();
    const existing = [
      ...areas.map((area) => area.name),
      ...projects.flatMap((project) => (project.area ? [project.area] : [])),
    ];
    if (existing.some((area) => area.toLocaleLowerCase() === trimmed.toLocaleLowerCase())) {
      setError("An area with this name already exists.");
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setError(null);
    try {
      await services.areas.create({ name: trimmed });
      onClose();
    } catch {
      setError("Could not create the area. Please try again.");
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !inFlight.current) onClose();
      }}
    >
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>New area</Dialog.Title>
          <Dialog.Description>
            Group projects around a part of your life, like Work or Family.
          </Dialog.Description>
        </Dialog.Header>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <label htmlFor={id} className="flex flex-col gap-2 text-sm font-medium">
            Area name
            <Input
              id={id}
              autoFocus
              value={name}
              placeholder="e.g. Hobbies"
              disabled={saving}
              onChange={(event) => {
                setName(event.target.value);
                setError(null);
              }}
            />
          </label>
          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
          <Dialog.Footer>
            <Button variant="ghost" disabled={saving} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || isLoading || projectsLoading || !name.trim()}>
              {saving ? "Creating…" : "Create area"}
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}
